import React, { useState, useEffect, useMemo } from "react";
import { useNavigate, useOutletContext } from "react-router-dom";
import "./BudgetComponent.scss";
import ButtonComponent from "../ButtonComponent/ButtonComponent";
import BlockDisplayComponent from "../BlockDisplayComponent/BlockDisplayComponent";

import { auth, db } from "../firebase/firebase";
import { doc, setDoc, getDoc, onSnapshot } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import {
  DEFAULT_THEME,
  createThemeStyle,
  sanitizeTheme,
} from "../utils/themeUtils";
import {
  FaWallet,
  FaMoneyBillWave,
  FaReceipt,
  FaChartLine,
  FaPlus,
  FaPen,
  FaTrash,
  FaCheckCircle,
  FaClock,
  FaRegStickyNote,
  FaBuilding,
  FaLayerGroup,
  FaCalendarAlt,
} from "react-icons/fa";

const ITEM_CATEGORIES = [
  "Venue",
  "Catering",
  "Photography",
  "Videography",
  "Decor",
  "Entertainment",
  "Clothing",
  "Jewelry",
  "Makeup",
  "Transport",
  "Invitations",
  "Gifts",
  "Other",
];

const PAID_STATUS_OPTIONS = [
  "Unpaid",
  "Deposit Paid",
  "Partially Paid",
  "Paid",
];

const createEmptyItemForm = () => ({
  name: "",
  category: "",
  vendor: "",
  dueDate: "",
  amount: "",
  deposit: "",
  balance: "",
  paidStatus: "Unpaid",
  notes: "",
});

const normalizeItem = (item = {}) => {
  const amount = Number(item.amount || 0);
  const deposit = Number(item.deposit || 0);
  const rawBalance =
    item.balance !== undefined && item.balance !== ""
      ? Number(item.balance || 0)
      : amount - deposit;

  return {
    name: item.name || "",
    category: item.category || "",
    vendor: item.vendor || "",
    dueDate: item.dueDate || "",
    amount: String(item.amount ?? ""),
    deposit: String(item.deposit ?? ""),
    balance: String(rawBalance >= 0 ? rawBalance : 0),
    paidStatus: item.paidStatus || "Unpaid",
    notes: item.notes || "",
  };
};

const BudgetComponent = () => {
  const navigate = useNavigate();
  const { weddingData, slug } = useOutletContext() || {};

  const [uid, setUid] = useState(null);
  const [loading, setLoading] = useState(true);

  const [siteTheme, setSiteTheme] = useState(null);
  const [themeLoading, setThemeLoading] = useState(true);

  const [plan, setPlan] = useState(null);
  const isTrial = plan === "trial";
  const MAX_ITEMS = isTrial ? 5 : Infinity;

  const [budget, setBudget] = useState("");
  const [expenses, setExpenses] = useState([]);
  const [payments, setPayments] = useState([]);

  const [expenseForm, setExpenseForm] = useState(createEmptyItemForm());
  const [paymentForm, setPaymentForm] = useState(createEmptyItemForm());

  const [editingExpenseIndex, setEditingExpenseIndex] = useState(null);
  const [editingPaymentIndex, setEditingPaymentIndex] = useState(null);

  const [editingExpenseForm, setEditingExpenseForm] = useState(
    createEmptyItemForm()
  );
  const [editingPaymentForm, setEditingPaymentForm] = useState(
    createEmptyItemForm()
  );

  const isPremiumSite = weddingData?.plan === "premium";
  const budgetAddonEnabled = weddingData?.addons?.budgetCalculation === true;
  const canAccessBudget = !isPremiumSite || budgetAddonEnabled;

  useEffect(() => {
    let unsubscribeUser = null;

    const unsubscribeAuth = onAuthStateChanged(auth, async (user) => {
      if (user) {
        setUid(user.uid);

        try {
          const userRef = doc(db, "users", user.uid);

          unsubscribeUser = onSnapshot(userRef, (docSnap) => {
            if (docSnap.exists()) {
              const data = docSnap.data();
              setPlan(data.plan || "trial");
            } else {
              setPlan("trial");
            }
            setLoading(false);
          });

          const budgetRef = doc(db, "users", user.uid, "budget", "data");
          const snap = await getDoc(budgetRef);

          if (snap.exists()) {
            const data = snap.data();
            setBudget(data.budget || "");
            setExpenses(
              Array.isArray(data.expenses)
                ? data.expenses.map(normalizeItem)
                : []
            );
            setPayments(
              Array.isArray(data.payments)
                ? data.payments.map(normalizeItem)
                : []
            );
          } else {
            setLoading(false);
          }
        } catch (err) {
          console.error("Error loading data:", err);
          setLoading(false);
        }
      } else {
        setLoading(false);
      }
    });

    return () => {
      unsubscribeAuth();
      if (unsubscribeUser) unsubscribeUser();
    };
  }, []);

  useEffect(() => {
    let isMounted = true;

    const loadTheme = async () => {
      try {
        setThemeLoading(true);

        const fallbackTheme = sanitizeTheme(weddingData?.theme || DEFAULT_THEME);
        const siteSlug = slug || weddingData?.slug || "";

        if (!siteSlug) {
          if (isMounted) setSiteTheme(fallbackTheme);
          return;
        }

        const siteSnap = await getDoc(doc(db, "sites", siteSlug));

        if (!isMounted) return;

        if (siteSnap.exists()) {
          const siteData = siteSnap.data();
          setSiteTheme(
            sanitizeTheme(siteData?.theme || weddingData?.theme || DEFAULT_THEME)
          );
        } else {
          setSiteTheme(fallbackTheme);
        }
      } catch (error) {
        console.error("Error loading budget theme:", error);

        if (isMounted) {
          setSiteTheme(sanitizeTheme(weddingData?.theme || DEFAULT_THEME));
        }
      } finally {
        if (isMounted) {
          setThemeLoading(false);
        }
      }
    };

    loadTheme();

    return () => {
      isMounted = false;
    };
  }, [weddingData, slug]);

  const themeStyle = useMemo(
    () => createThemeStyle(siteTheme || DEFAULT_THEME),
    [siteTheme]
  );

  const saveToFirebase = async (updatedData) => {
    if (!uid) return;

    try {
      const ref = doc(db, "users", uid, "budget", "data");
      await setDoc(ref, updatedData);
    } catch (error) {
      console.error("Error saving:", error);
    }
  };

  const handleChange = (setter) => (e) => {
    const { name, value } = e.target;

    setter((prev) => {
      const next = { ...prev, [name]: value };

      if (name === "amount" || name === "deposit") {
        const amount = Number(next.amount || 0);
        const deposit = Number(next.deposit || 0);
        const balance = Math.max(amount - deposit, 0);
        next.balance = String(balance);
      }

      if (name === "paidStatus") {
        if (value === "Paid") {
          next.deposit = next.amount || next.deposit;
          next.balance = "0";
        }
      }

      return next;
    });
  };

  const resetExpenseForm = () => setExpenseForm(createEmptyItemForm());
  const resetPaymentForm = () => setPaymentForm(createEmptyItemForm());
  const resetEditingExpenseForm = () => setEditingExpenseForm(createEmptyItemForm());
  const resetEditingPaymentForm = () => setEditingPaymentForm(createEmptyItemForm());

  const addExpense = async () => {
    if (!expenseForm.name.trim() || !expenseForm.amount) return;
    if (expenses.length >= MAX_ITEMS) return;

    const updatedExpenses = [...expenses, normalizeItem(expenseForm)];

    setExpenses(updatedExpenses);
    resetExpenseForm();

    await saveToFirebase({
      budget,
      expenses: updatedExpenses,
      payments,
    });
  };

  const addPayment = async () => {
    if (!paymentForm.name.trim() || !paymentForm.amount) return;
    if (payments.length >= MAX_ITEMS) return;

    const updatedPayments = [...payments, normalizeItem(paymentForm)];

    setPayments(updatedPayments);
    resetPaymentForm();

    await saveToFirebase({
      budget,
      expenses,
      payments: updatedPayments,
    });
  };

  const startEditExpense = (index) => {
    setEditingExpenseIndex(index);
    setEditingExpenseForm(normalizeItem(expenses[index]));
  };

  const cancelEditExpense = () => {
    setEditingExpenseIndex(null);
    resetEditingExpenseForm();
  };

  const saveExpenseEdit = async (index) => {
    if (!editingExpenseForm.name.trim() || !editingExpenseForm.amount) return;

    const updatedExpenses = [...expenses];
    updatedExpenses[index] = normalizeItem(editingExpenseForm);

    setExpenses(updatedExpenses);
    cancelEditExpense();

    await saveToFirebase({
      budget,
      expenses: updatedExpenses,
      payments,
    });
  };

  const deleteExpense = async (index) => {
    const updatedExpenses = expenses.filter((_, i) => i !== index);
    setExpenses(updatedExpenses);

    if (editingExpenseIndex === index) {
      cancelEditExpense();
    }

    await saveToFirebase({
      budget,
      expenses: updatedExpenses,
      payments,
    });
  };

  const startEditPayment = (index) => {
    setEditingPaymentIndex(index);
    setEditingPaymentForm(normalizeItem(payments[index]));
  };

  const cancelEditPayment = () => {
    setEditingPaymentIndex(null);
    resetEditingPaymentForm();
  };

  const savePaymentEdit = async (index) => {
    if (!editingPaymentForm.name.trim() || !editingPaymentForm.amount) return;

    const updatedPayments = [...payments];
    updatedPayments[index] = normalizeItem(editingPaymentForm);

    setPayments(updatedPayments);
    cancelEditPayment();

    await saveToFirebase({
      budget,
      expenses,
      payments: updatedPayments,
    });
  };

  const deletePayment = async (index) => {
    const updatedPayments = payments.filter((_, i) => i !== index);
    setPayments(updatedPayments);

    if (editingPaymentIndex === index) {
      cancelEditPayment();
    }

    await saveToFirebase({
      budget,
      expenses,
      payments: updatedPayments,
    });
  };

  const totalBudget = useMemo(() => Number(budget || 0), [budget]);

  const totalExpenses = useMemo(
    () => expenses.reduce((acc, item) => acc + Number(item.amount || 0), 0),
    [expenses]
  );

  const totalPayments = useMemo(
    () => payments.reduce((acc, item) => acc + Number(item.amount || 0), 0),
    [payments]
  );

  const totalDeposits = useMemo(
    () =>
      [...expenses, ...payments].reduce(
        (acc, item) => acc + Number(item.deposit || 0),
        0
      ),
    [expenses, payments]
  );

  const totalBalances = useMemo(
    () =>
      [...expenses, ...payments].reduce(
        (acc, item) => acc + Number(item.balance || 0),
        0
      ),
    [expenses, payments]
  );

  const remaining = useMemo(
    () => totalBudget - totalPayments,
    [totalBudget, totalPayments]
  );

  const pendingExpenses = useMemo(
    () => totalExpenses - totalPayments,
    [totalExpenses, totalPayments]
  );

  const budgetUsagePercent = useMemo(() => {
    if (!totalBudget) return 0;
    return Math.min((totalExpenses / totalBudget) * 100, 100);
  }, [totalBudget, totalExpenses]);

  const paymentCoveragePercent = useMemo(() => {
    if (!totalExpenses) return 0;
    return Math.min((totalPayments / totalExpenses) * 100, 100);
  }, [totalExpenses, totalPayments]);

  const dueSoonCount = useMemo(() => {
    const today = new Date();
    const sevenDaysLater = new Date();
    sevenDaysLater.setDate(today.getDate() + 7);

    return [...expenses, ...payments].filter((item) => {
      if (!item.dueDate) return false;
      const due = new Date(item.dueDate);
      return due >= today && due <= sevenDaysLater;
    }).length;
  }, [expenses, payments]);

  const formatAmount = (value) => {
    const number = Number(value || 0);
    return number.toLocaleString();
  };

  const renderItemDetails = (item) => (
    <div className="budget__detailGrid">
      <div className="budget__detailTag">
        <FaLayerGroup />
        <span>{item.category || "No category"}</span>
      </div>

      <div className="budget__detailTag">
        <FaBuilding />
        <span>{item.vendor || "No vendor"}</span>
      </div>

      <div className="budget__detailTag">
        <FaCalendarAlt />
        <span>{item.dueDate || "No due date"}</span>
      </div>

      <div className="budget__detailTag">
        <FaCheckCircle />
        <span>{item.paidStatus || "Unpaid"}</span>
      </div>
    </div>
  );

  const renderItemAmounts = (item) => (
    <div className="budget__amountSplit">
      <div className="budget__amountMini">
        <span>Total</span>
        <strong>{formatAmount(item.amount)}</strong>
      </div>
      <div className="budget__amountMini">
        <span>Deposit</span>
        <strong>{formatAmount(item.deposit)}</strong>
      </div>
      <div className="budget__amountMini">
        <span>Balance</span>
        <strong>{formatAmount(item.balance)}</strong>
      </div>
    </div>
  );

  const renderNotes = (item) =>
    item.notes ? (
      <div className="budget__notes">
        <FaRegStickyNote />
        <p>{item.notes}</p>
      </div>
    ) : null;

  const renderFormFields = (formState, setter) => (
    <div className="budget__richForm">
      <input
        name="name"
        placeholder="Name"
        value={formState.name}
        onChange={handleChange(setter)}
        className="budget__input"
      />

      <select
        name="category"
        value={formState.category}
        onChange={handleChange(setter)}
        className="budget__input"
      >
        <option value="">Select category</option>
        {ITEM_CATEGORIES.map((category) => (
          <option key={category} value={category}>
            {category}
          </option>
        ))}
      </select>

      <input
        name="vendor"
        placeholder="Vendor"
        value={formState.vendor}
        onChange={handleChange(setter)}
        className="budget__input"
      />

      <input
        name="dueDate"
        type="date"
        value={formState.dueDate}
        onChange={handleChange(setter)}
        className="budget__input"
      />

      <input
        name="amount"
        type="number"
        placeholder="Total amount"
        value={formState.amount}
        onChange={handleChange(setter)}
        className="budget__input"
      />

      <input
        name="deposit"
        type="number"
        placeholder="Deposit"
        value={formState.deposit}
        onChange={handleChange(setter)}
        className="budget__input"
      />

      <input
        name="balance"
        type="number"
        placeholder="Balance"
        value={formState.balance}
        onChange={handleChange(setter)}
        className="budget__input"
      />

      <select
        name="paidStatus"
        value={formState.paidStatus}
        onChange={handleChange(setter)}
        className="budget__input"
      >
        {PAID_STATUS_OPTIONS.map((status) => (
          <option key={status} value={status}>
            {status}
          </option>
        ))}
      </select>

      <textarea
        name="notes"
        placeholder="Notes / reminders"
        value={formState.notes}
        onChange={handleChange(setter)}
        className="budget__textarea"
        rows={3}
      />
    </div>
  );

  if (loading || plan === null || themeLoading || !siteTheme) {
    return (
      <div className="budget__loading" style={themeStyle}>
        <div className="budget__loader"></div>
        <p>Loading your event budget...</p>
      </div>
    );
  }

  if (!canAccessBudget) {
    return (
      <BlockDisplayComponent
        title="This feature is locked"
        description="Premium users can access it only when this add-on has been added to their package. Call 070 536 7980 to get this feature"
        primaryButtonText="Add this feature"
        secondaryButtonText="Go Back"
        onPrimaryClick={() => navigate("/premium")}
        onSecondaryClick={() => navigate(-1)}
      />
    );
  }

  return (
    <div className="budget" style={themeStyle}>
      <div className="budget__container">
        <div className="budget__hero">
          <div className="budget__heroText">
            <p className="budget__eyebrow">Financial Planner</p>
            <h2 className="budget__title">Event Budget</h2>
            <p className="budget__subtitle">
              Track your expenses, payments, deposits, balances, due dates, vendors,
              and reminders in one place.
            </p>
          </div>

          {isTrial && (
            <div className="budget__trial">
              Trial plan: Max 5 expenses and 5 payments
            </div>
          )}
        </div>

        <div className="budget__top">
          <div className="budgetCard budgetCard--budget">
            <div className="budgetCard__icon">
              <FaWallet />
            </div>

            <div className="budgetCard__content">
              <label className="budget__label">Total Budget</label>
              <input
                type="number"
                value={budget}
                placeholder="Enter total budget"
                className="budget__input"
                onChange={async (e) => {
                  const value = e.target.value;
                  setBudget(value);

                  await saveToFirebase({
                    budget: value,
                    expenses,
                    payments,
                  });
                }}
              />
            </div>
          </div>

          <div className="budget__stats">
            <div className="budgetStat">
              <div className="budgetStat__icon">
                <FaReceipt />
              </div>
              <p>Expenses</p>
              <h4>{formatAmount(totalExpenses)}</h4>
            </div>

            <div className="budgetStat">
              <div className="budgetStat__icon">
                <FaMoneyBillWave />
              </div>
              <p>Payments</p>
              <h4>{formatAmount(totalPayments)}</h4>
            </div>

            <div className="budgetStat budgetStat--highlight">
              <div className="budgetStat__icon">
                <FaChartLine />
              </div>
              <p>Remaining Budget</p>
              <h4>{formatAmount(remaining)}</h4>
            </div>
          </div>
        </div>

        <div className="budget__overview">
          <div className="budgetOverviewCard">
            <div className="budgetOverviewCard__head">
              <span>Budget Used</span>
              <strong>{budgetUsagePercent.toFixed(0)}%</strong>
            </div>
            <div className="budgetOverviewCard__bar">
              <div
                className="budgetOverviewCard__fill"
                style={{ width: `${budgetUsagePercent}%` }}
              />
            </div>
            <small>
              {formatAmount(totalExpenses)} of {formatAmount(totalBudget)}
            </small>
          </div>

          <div className="budgetOverviewCard">
            <div className="budgetOverviewCard__head">
              <span>Payments Coverage</span>
              <strong>{paymentCoveragePercent.toFixed(0)}%</strong>
            </div>
            <div className="budgetOverviewCard__bar">
              <div
                className="budgetOverviewCard__fill budgetOverviewCard__fill--success"
                style={{ width: `${paymentCoveragePercent}%` }}
              />
            </div>
            <small>
              {formatAmount(totalPayments)} of {formatAmount(totalExpenses)}
            </small>
          </div>

          <div className="budgetMiniStats">
            <div className="budgetMiniStat">
              <div className="budgetMiniStat__icon">
                <FaClock />
              </div>
              <div>
                <span>Pending</span>
                <strong>{formatAmount(pendingExpenses)}</strong>
              </div>
            </div>

            <div className="budgetMiniStat">
              <div className="budgetMiniStat__icon">
                <FaCheckCircle />
              </div>
              <div>
                <span>Due Soon</span>
                <strong>{dueSoonCount}</strong>
              </div>
            </div>

            <div className="budgetMiniStat">
              <div className="budgetMiniStat__icon">
                <FaMoneyBillWave />
              </div>
              <div>
                <span>Deposits</span>
                <strong>{formatAmount(totalDeposits)}</strong>
              </div>
            </div>

            <div className="budgetMiniStat">
              <div className="budgetMiniStat__icon">
                <FaWallet />
              </div>
              <div>
                <span>Balances</span>
                <strong>{formatAmount(totalBalances)}</strong>
              </div>
            </div>
          </div>
        </div>

        <div className="budget__grid">
          <div className="budgetPanel">
            <div className="budgetPanel__header">
              <div>
                <h3>Expenses</h3>
                <p>Track all wedding-related costs</p>
              </div>
              {isTrial && (
                <span className="budgetPanel__badge">
                  {expenses.length}/{MAX_ITEMS}
                </span>
              )}
            </div>

            {renderFormFields(expenseForm, setExpenseForm)}

            <div className="budget__formActions">
              <ButtonComponent
                onClick={addExpense}
                className="budget__addBtn"
                disabled={isTrial && expenses.length >= MAX_ITEMS}
              >
                <FaPlus style={{ marginRight: 8 }} />
                Add Expense
              </ButtonComponent>
            </div>

            <div className="budget__list">
              {expenses.length === 0 ? (
                <div className="budget__empty">No expenses added yet.</div>
              ) : (
                expenses.map((item, i) => (
                  <div key={i} className="budget__tile">
                    {editingExpenseIndex === i ? (
                      <>
                        {renderFormFields(
                          editingExpenseForm,
                          setEditingExpenseForm
                        )}

                        <div className="budget__tileActions budget__tileActions--edit">
                          <button
                            type="button"
                            className="budget__actionBtn budget__actionBtn--save"
                            onClick={() => saveExpenseEdit(i)}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="budget__actionBtn budget__actionBtn--ghost"
                            onClick={cancelEditExpense}
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="budget__tileTop">
                          <div className="budget__tileLeft">
                            <span className="budget__tileName">{item.name}</span>
                            <small className="budget__tileMeta">Expense item</small>
                          </div>

                          <div className="budget__tileActions">
                            <button
                              type="button"
                              className="budget__iconBtn"
                              onClick={() => startEditExpense(i)}
                              aria-label="Edit expense"
                            >
                              <FaPen />
                            </button>
                            <button
                              type="button"
                              className="budget__iconBtn budget__iconBtn--danger"
                              onClick={() => deleteExpense(i)}
                              aria-label="Delete expense"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </div>

                        {renderItemDetails(item)}
                        {renderItemAmounts(item)}
                        {renderNotes(item)}
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>

          <div className="budgetPanel">
            <div className="budgetPanel__header">
              <div>
                <h3>Payments</h3>
                <p>Track amounts already paid</p>
              </div>
              {isTrial && (
                <span className="budgetPanel__badge">
                  {payments.length}/{MAX_ITEMS}
                </span>
              )}
            </div>

            {renderFormFields(paymentForm, setPaymentForm)}

            <div className="budget__formActions">
              <ButtonComponent
                onClick={addPayment}
                className="budget__addBtn"
                disabled={isTrial && payments.length >= MAX_ITEMS}
              >
                <FaPlus style={{ marginRight: 8 }} />
                Add Payment
              </ButtonComponent>
            </div>

            <div className="budget__list">
              {payments.length === 0 ? (
                <div className="budget__empty">No payments added yet.</div>
              ) : (
                payments.map((item, i) => (
                  <div key={i} className="budget__tile">
                    {editingPaymentIndex === i ? (
                      <>
                        {renderFormFields(
                          editingPaymentForm,
                          setEditingPaymentForm
                        )}

                        <div className="budget__tileActions budget__tileActions--edit">
                          <button
                            type="button"
                            className="budget__actionBtn budget__actionBtn--save"
                            onClick={() => savePaymentEdit(i)}
                          >
                            Save
                          </button>
                          <button
                            type="button"
                            className="budget__actionBtn budget__actionBtn--ghost"
                            onClick={cancelEditPayment}
                          >
                            Cancel
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="budget__tileTop">
                          <div className="budget__tileLeft">
                            <span className="budget__tileName">{item.name}</span>
                            <small className="budget__tileMeta">Payment entry</small>
                          </div>

                          <div className="budget__tileActions">
                            <button
                              type="button"
                              className="budget__iconBtn"
                              onClick={() => startEditPayment(i)}
                              aria-label="Edit payment"
                            >
                              <FaPen />
                            </button>
                            <button
                              type="button"
                              className="budget__iconBtn budget__iconBtn--danger"
                              onClick={() => deletePayment(i)}
                              aria-label="Delete payment"
                            >
                              <FaTrash />
                            </button>
                          </div>
                        </div>

                        {renderItemDetails(item)}
                        {renderItemAmounts(item)}
                        {renderNotes(item)}
                      </>
                    )}
                  </div>
                ))
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BudgetComponent;